import React, { useCallback, useContext, useEffect, useRef } from 'react';
import DashboardContainer from 'views/Dashboard/DashboardContainer';
import styles from './farmProductPage.module.scss';
import { CommodityTabsKeys, FarmPageProps } from 'enums/FarmPageEnum';
import COSearch from 'shared/components/COSearch';
import { SearchProps } from 'enums/ProductsViewEnums';
import COTabs from 'shared/components/COTabs';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import COPagination from 'shared/components/COPagination';
import {
  CommodityStatusParams,
  GetCommodityTabsProps,
  IN_REVIEW_ROUTE,
  TAB_CONFIGS,
  TabLabelProps,
  inReviewStatus,
  socketNotificationLabel,
  statusMapping,
  validNotificationEvent,
} from './constant';
import useFarmboxRedirections from 'shared/utils/useFarmboxRedirections';
import CommodityListService from 'services/CommodityListService/commodityList.service';
import clsx from 'clsx';
import CommodityProductLists from 'shared/components/CommodityProducts/ProductList';
import { debounce } from 'lodash';
import { SocketContext } from 'context/SocketContext';
import { InAppNotification } from 'models/InAppNotification/inAppNotification.model';
import { deserialize } from 'serializr';
import { CommodityTypes } from 'constants/FarmboxConstants/stage0.constants';

const FarmProductPage: React.FC = () => {
  const navigate = useNavigate();

  const { status } = useParams<{ status: string }>();

  const isActiveTab = (key: string) => status === key;

  const currentTab = status || CommodityTabsKeys.ALL_COMMODITIES;

  const mappedStatus = statusMapping[currentTab];

  const [searchParams] = useSearchParams();

  const { redirectToFarm } = useFarmboxRedirections();

  const { fetchCommodityList, commodityListData, loading, deleteCommodity } =
    CommodityListService();

  const searchTermRef = useRef<string>('');

  const { socket } = useContext(SocketContext);

  const page = parseInt(
    searchParams.get(CommodityStatusParams.PAGE) ||
      CommodityStatusParams.PAGE_DEFAULT,
    10
  );
  const limit = parseInt(
    searchParams.get(CommodityStatusParams.LIMIT) ||
      CommodityStatusParams.LIMIT_DEFAULT,
    10
  );

  const fetchData = useCallback(() => {
    fetchCommodityList({
      status: mappedStatus === IN_REVIEW_ROUTE ? inReviewStatus : mappedStatus,
      page,
      search: searchTermRef.current,
    });
  }, [fetchCommodityList, mappedStatus, page]);

  useEffect(() => {
    fetchData();
  }, [currentTab, page]);

  useEffect(() => {
    if (!socket) return;

    const handleNotification = (data: InAppNotification) => {
      const notification = deserialize(InAppNotification, data);

      if (
        notification.event &&
        validNotificationEvent.includes(notification.event)
      ) {
        fetchData();
      }
    };

    socket.on(socketNotificationLabel, handleNotification);

    return () => {
      socket.off(socketNotificationLabel, handleNotification);
    };
  }, [socket]);

  const debouncedFetchCommodityList = useCallback(
    debounce(status => {
      fetchCommodityList({
        status:
          mappedStatus === IN_REVIEW_ROUTE ? inReviewStatus : mappedStatus,
        page,
        search: searchTermRef.current,
      });
    }, 500),
    [mappedStatus, page]
  );

  const handleDelete = async (id: number, type: string) => {
    await deleteCommodity(id, type === CommodityTypes.LIVE_STOCK);
    fetchCommodityList({
      status: mappedStatus,
      page,
      search: searchTermRef.current,
    });
  };

  const TabLabel = ({ icon: Icon, label, isActive }: TabLabelProps) => {
    return (
      <span
        className={clsx({
          [styles.tabContainer]: true,
          [styles.tabActive]: isActive,
        })}>
        <Icon
          className={clsx({
            [styles.icon]: isActive,
          })}
        />
        {label}
      </span>
    );
  };

  const getCommodityTabs = ({ isActiveTab }: GetCommodityTabsProps) =>
    TAB_CONFIGS.map(({ key, icon, label }) => ({
      children: (
        <CommodityProductLists
          currentTab={key}
          commodityLoading={loading}
          commodityDataList={commodityListData}
          handleDelete={handleDelete}
        />
      ),
      active: isActiveTab(key),
      label: <TabLabel icon={icon} label={label} isActive={isActiveTab(key)} />,
      key,
    }));

  const CommodityTabsComponents = getCommodityTabs({
    isActiveTab,
  });

  const handleTabChange = (key: string) => {
    navigate(`/${CommodityTabsKeys.FARM}/${key}`, {
      replace: true,
    });
  };

  const handlePaginationChange = (pageNumber: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(CommodityStatusParams.PAGE, String(pageNumber));
    newParams.set(CommodityStatusParams.LIMIT, String(limit));

    const queryParamsObject = Object.fromEntries(newParams.entries());

    redirectToFarm(currentTab, queryParamsObject);

    fetchCommodityList({
      status: mappedStatus,
      page: pageNumber,
      search: searchTermRef.current,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const activeTab = e.target.value;
    searchTermRef.current = activeTab;
    debouncedFetchCommodityList(activeTab);
  };

  return (
    <DashboardContainer>
      <div className={styles.headerContainer}>
        <div className={styles.headerLeftContainer}>
          <h1>{FarmPageProps.PAGE_TITLE}</h1>
          <div className={styles.searchContainer}>
            <COSearch
              name={SearchProps.SEARCH_NAME}
              placeholderString={SearchProps.PLACEHOLDER_STRING}
              handleChange={handleInputChange}
            />
          </div>
        </div>
        <COTabs
          components={CommodityTabsComponents}
          activeKey={currentTab}
          handleChange={handleTabChange}
          tabBarExtraContent={
            commodityListData?.meta?.totalPages ?? 1 > 1
              ? {
                  right: (
                    <COPagination
                      onChange={handlePaginationChange}
                      total={commodityListData?.meta?.totalItems ?? 0}
                      current={commodityListData?.meta?.currentPage}
                      pageSize={commodityListData?.meta?.pageSize}
                    />
                  ),
                }
              : undefined
          }
        />
      </div>
    </DashboardContainer>
  );
};

export default FarmProductPage;
